/**
 * @class ImageStore
 */
export default class ImageStore {
    /**
     * Store of images from main DB.
     */
    _mainImageList = {};

    /**
     * Store of database images.
     */
    _dbImageList = {};

    /**
     * Store of print images.
     */
    _printImageList = {};

    /**
     * Store of color in fandeck preview images.
     */
    _cifImageList = {};

    /**
     * Store of fandeck preview images.
     */
    _fandeckImageList = {};

    /**
     * Store of formula note images.
     */
    _formulaNoteImageList = {};

    /**
     * Store of product file images.
     */
    _productFileImageList = {};

    /**
     * Store of product file online images.
     */
    _productFileImageOnlineList = {};

    /**
     * Store of tintometric system images.
     */
    _tsImageList = {};

    /**
     * Colorant package image
     */
    _colorantPackage = null;

    /**
     * Machine canister image
     */
    _machineCanister = null;

    /**
     * Machine error image
     */
    _machineError = null;

    /**
     * Array of banner images
     */
    _bannerList = [];

    /**
     * Array of temp banner images
     */
    _tempBannerList = [];

    /**
     * Store of images for barcode test modal
     */
    _barcodeTestImageList = {};

    constructor() {
        this._mainImageList = {};
        this._dbImageList = {};
        this._printImageList = {};
        this._cifImageList = {};
        this._fandeckImageList = {};
        this._formulaNoteImageList = {};
        this._productFileImageList = {};
        this._productFileImageOnlineList = {};
        this._tsImageList = {};
        this._colorantPackage = null;
        this._machineCanister = null;
        this._machineError = null;
        this._bannerList = [];
        this._tempBannerList = [];
        this._barcodeTestImageList = {};
    }

    /**
     * @param   {Number}    imageId
     * @returns {Object}
     */
    getImageById = (imageId) => {
        if (this._mainImageList[imageId] === undefined) {
            return null;
        }

        return this._mainImageList[imageId];
    };

    /**
     * @param   {Number}    imageId
     * @param   {Object}    image
     */
    addImage = (imageId, image) => {
        this._mainImageList[imageId] = image;
    };

    /**
     * @param   {Number}    itemType
     * @returns {Object}
     */
    getBarcodeTestImage = (itemType) => {
        if (this._barcodeTestImageList[itemType] === undefined) {
            return null;
        }

        return this._barcodeTestImageList[itemType];
    };

    /**
     * @param   {Number}    itemType
     * @param   {Object}    image
     */
    addBarcodeTestImage = (itemType, image) => {
        this._barcodeTestImageList[itemType] = image;
    };

    /**
     * @param   {Number}    imageId
     */
    removeImage = (imageId) => {
        if (this._mainImageList[imageId] !== undefined) {
            delete this._mainImageList[imageId];
        }
    };

    /**
     * @param   {String}    dbName
     * @returns {Object}
     */
    getDbImageByName = (dbName) => {
        if (this._dbImageList[dbName] === undefined) {
            return null;
        }

        return this._dbImageList[dbName];
    };

    /**
     * @param   {String}    dbName
     * @param   {Object}    image
     */
    addDbImage = (dbName, image) => {
        this._dbImageList[dbName] = image;
    };

    /**
     * @param   {String}    templateName
     * @returns {Object}
     */
    getPrintImageByName = (templateName) => {
        if (this._printImageList[templateName] === undefined) {
            return null;
        }

        return this._printImageList[templateName];
    };

    /**
     * @param   {String}    templateName
     * @param   {Object}    image
     */
    addPrintImage = (templateName, image) => {
        this._printImageList[templateName] = image;
    };

    /**
     * @param   {Number}    fandeckId
     * @param   {Number}    colorId
     * @returns {Object}
     */
    getCifImageById = (fandeckId, colorId) => {
        if (this._cifImageList[fandeckId + "-" + colorId] === undefined) {
            return null;
        }

        return this.getImageById(this._cifImageList[fandeckId + "-" + colorId]);
    };

    /**
     * @param   {Number}    fandeckId
     * @param   {Number}    colorId
     * @param   {Number}    imageId
     * @param   {Object}    image
     */
    addCifImage = (fandeckId, colorId, imageId, image) => {
        this._cifImageList[fandeckId + "-" + colorId] = imageId;
        this.addImage(imageId, image);
    };

    /**
     * @param   {String}    key
     */
    removeCifImage = (key) => {
        if (this._cifImageList[key] !== undefined) {
            delete this._cifImageList[key];
        }
    };

    /**
     * @param   {Number}    fandeckId
     * @returns {Object}
     */
    getFandeckImageById = (fandeckId) => {
        if (this._fandeckImageList[fandeckId] === undefined) {
            return null;
        }

        return this._fandeckImageList[fandeckId];
    };

    /**
     * @param   {Number}    fandeckId
     * @param   {Object}    image
     */
    addFandeckImage = (fandeckId, image) => {
        this._fandeckImageList[fandeckId] = image;
    };

    /**
     * @param   {Number}    fandeckId
     */
    removeFandeckImage = (fandeckId) => {
        if (this._fandeckImageList[fandeckId] !== undefined) {
            delete this._fandeckImageList[fandeckId];
        }
    };

    /**
     * @param   {Number}    noteId
     * @returns {Object}
     */
    getFormulaNoteImageById = (noteId) => {
        if (this._formulaNoteImageList[noteId] === undefined) {
            return null;
        }

        return this._formulaNoteImageList[noteId];
    };

    /**
     * @param   {Number}    noteId
     * @param   {Object}    image
     */
    addFormulaNoteImage = (noteId, image) => {
        this._formulaNoteImageList[noteId] = image;
    };

    /**
     * @param   {Number}    noteId
     */
    removeFormulaNoteImage = (noteId) => {
        if (this._formulaNoteImageList[noteId] !== undefined) {
            delete this._formulaNoteImageList[noteId];
        }
    };

    /**
     * @param   {Number}    fileId
     * @param   {Number}    page
     * @param   {Boolean}   online
     * @returns {Object}
     */
    getProductFileImageById = (fileId, page, online) => {
        if (online) {
            if (
                this._productFileImageOnlineList[fileId] === undefined ||
                this._productFileImageOnlineList[fileId][page] === undefined
            ) {
                return null;
            }

            return this._productFileImageOnlineList[fileId][page];
        }

        if (
            this._productFileImageList[fileId] === undefined ||
            this._productFileImageList[fileId][page] === undefined
        ) {
            return null;
        }

        return this._productFileImageList[fileId][page];
    };

    /**
     * @param   {Number}    fileId
     * @param   {Number}    page
     * @param   {Boolean}   online
     * @param   {Object}    image
     */
    addProductFileImage = (fileId, page, online, image) => {
        if (online) {
            if (this._productFileImageOnlineList[fileId] === undefined) {
                this._productFileImageOnlineList[fileId] = {};
            }

            this._productFileImageOnlineList[fileId][page] = image;
        } else {
            if (this._productFileImageList[fileId] === undefined) {
                this._productFileImageList[fileId] = {};
            }

            this._productFileImageList[fileId][page] = image;
        }
    };

    /**
     * @param   {Number}    fileId
     */
    removeProductFileImage = (fileId) => {
        if (this._productFileImageList[fileId] !== undefined) {
            delete this._productFileImageList[fileId];
        }
    };

    /**
     * @param   {Number}    fileId
     */
    removeProductFileImageOnline = (fileId) => {
        if (this._productFileImageOnlineList[fileId] !== undefined) {
            delete this._productFileImageOnlineList[fileId];
        }
    };

    /**
     * @param   {Number}    tsId
     * @returns {Object}
     */
    getTsImageById = (tsId) => {
        if (this._tsImageList[tsId] === undefined) {
            return null;
        }

        return this._tsImageList[tsId];
    };

    /**
     * @param   {Number}    tsId
     * @param   {Object}    image
     */
    addTsImage = (tsId, image) => {
        this._tsImageList[tsId] = image;
    };

    /**
     * @param   {Number}    dbName
     * @returns {Boolean}
     */
    dbImageIsEmpty = (dbName) => {
        return this._dbImageList[dbName] === undefined;
    };

    /**
     * @returns {Object}
     */
    getColorantPackage = () => {
        return this._colorantPackage;
    };

    /**
     * @param   {Object}    colorantPackage
     */
    setColorantPackage = (colorantPackage) => {
        this._colorantPackage = colorantPackage;
    };

    /**
     * @returns {Object}
     */
    getMachineCanister = () => {
        return this._machineCanister;
    };

    /**
     * @param   {Object}    machineCanister
     */
    setMachineCanister = (machineCanister) => {
        this._machineCanister = machineCanister;
    };

    /**
     * @returns {Object}
     */
    getMachineError = () => {
        return this._machineError;
    };

    /**
     * @param   {Object}    machineError
     */
    setMachineError = (machineError) => {
        this._machineError = machineError;
    };

    /**
     * @returns {Array}
     */
    getBannerList = () => {
        return this._bannerList;
    };

    /**
     * @param   {Array}    bannerList
     */
    setBannerList = (bannerList) => {
        this._bannerList = bannerList;
    };

    /**
     * @returns {Array}
     */
    getTempBannerList = () => {
        return this._tempBannerList;
    };

    /**
     * @param   {Array} bannerList
     */
    setTempBannerList = (bannerList) => {
        this._tempBannerList = bannerList;
    };

    /**
     * @param   {Object}    bannerImage
     */
    addBannerImage = (bannerImage) => {
        this._bannerList.push(bannerImage);
    };

    /**
     * @param   {Array}     imageList
     * @param   {Array}     cifImageList
     * @param   {Array}     fandeckImageList
     * @param   {Array}     productFileImageList
     * @param   {Boolean}   colorTabActive
     * @param   {Boolean}   barcodeTestActive
     */
    cleanImageList = (
        imageList,
        cifImageList,
        fandeckImageList,
        productFileImageList,
        colorTabActive,
        barcodeTestActive
    ) => {
        let unactiveList = [];

        let imageId;

        if (!colorTabActive) {
            for (imageId in this._cifImageList) {
                if (cifImageList.includes(imageId)) {
                    imageList.push(this._cifImageList[imageId]);
                } else {
                    unactiveList.push(this._cifImageList[imageId]);
                }
            }

            for (imageId in this._mainImageList) {
                if (!imageList.includes(Number(imageId))) {
                    unactiveList.push(Number(imageId));
                }
            }

            for (imageId of unactiveList) {
                this.removeImage(imageId);
            }

            unactiveList.length = 0;

            for (imageId in this._cifImageList) {
                if (this._mainImageList[this._cifImageList[imageId]] === undefined) {
                    unactiveList.push(imageId);
                }
            }

            for (imageId of unactiveList) {
                this.removeCifImage(imageId);
            }

            unactiveList.length = 0;
        }

        for (imageId in this._fandeckImageList) {
            if (!fandeckImageList.includes(Number(imageId))) {
                unactiveList.push(Number(imageId));
            }
        }

        for (imageId of unactiveList) {
            this.removeFandeckImage(imageId);
        }

        unactiveList.length = 0;

        for (imageId in this._productFileImageList) {
            if (!productFileImageList.includes(Number(imageId))) {
                unactiveList.push(Number(imageId));
            }
        }

        for (imageId of unactiveList) {
            this.removeProductFileImage(imageId);
        }

        unactiveList.length = 0;

        for (imageId in this._productFileImageOnlineList) {
            if (!productFileImageList.includes(Number(imageId))) {
                unactiveList.push(Number(imageId));
            }
        }

        for (imageId of unactiveList) {
            this.removeProductFileImageOnline(imageId);
        }

        if (!barcodeTestActive && Object.keys(this._barcodeTestImageList).length > 0) {
            this._barcodeTestImageList = {};
        }
    };
}
